import { FiChevronDown, FiPlus } from 'react-icons/fi';
import {
    MdCheckBox,
    MdCheckBoxOutlineBlank,
    MdPlaylistAddCheck,
} from 'react-icons/md';
import MenuButton, { MenuSeparator } from './MenuButton';
import {
    useCountryParam,
    useFetchWatchlistItems,
    useFetchWatchlists,
    useMutateWatchlistItems,
    useMutateWatchlists,
} from 'utils/hooks';
import IconButton from './IconButton';
import SoftButton from './SoftButton';
import WatchlistModal from 'components/modals/CreateWatchlist';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';

const SoftButtonWhiteText = styled(SoftButton)`
    color: white;
    text-decoration: underline;
    padding: 0;
    margin-left: 8px;
    line-height: inherit;
    min-height: 0;

    &:hover,
    &:focus,
    &:focus-visible,
    &[data-state='hover'] {
        color: white;
        background: transparent;
        text-decoration: underline;
        box-shadow: none;
    }
`;

const NEW_WATCHLIST = 'New Watchlist';

function ToggleWatch({
    id,
    data,
    btnChildren = 'Add to Watchlist',
    mixpanelContext = 'ToggleWatch',
    ...restProps
}) {
    const { mutateToWithCountryParam } = useCountryParam();
    const history = useHistory();
    const { isLoading: isLoadingLists, data: watchlists } =
        useFetchWatchlists();
    const { isLoading: isLoadingItems, data: watchlistItems } =
        useFetchWatchlistItems();
    const [showModal, setShowModal] = useState(false);
    const { add: addWatchlist } = useMutateWatchlists();
    const { add: addItems, remove: removeItems } = useMutateWatchlistItems();
    const isLoading = isLoadingLists || isLoadingItems;
    const matchRelatedMeta = (item, data) => {
        const wantBrand = item.brandId != null;
        const wantCat = item.categoryId != null;
        // If both are present on `data`, require both to match.
        if (wantBrand && wantCat) {
            return (
                item.brandId === data.brandId &&
                item.categoryId === data.categoryId
            );
        }
        // Otherwise, match whichever exists.
        if (wantBrand) return item.brandId === data.brandId;
        if (wantCat) return item.categoryId === data.categoryId;
        return false;
    };
    const foundItemsByWatchlist =
        watchlistItems && watchlists
            ? watchlistItems.reduce((acc, item) => {
                  const { id: itemId, watchlist } = item;

                  if (!(watchlist in watchlists)) return acc;

                  const isHashtag =
                      item.type === 'hashtag' ||
                      item.type === 'instagram-hashtag';
                  const isRelatedHashtag =
                      item.type === 'related-hashtag' ||
                      item.type === 'instagram-related-hashtag';
                  const isTypeAndNameMatch =
                      item.type === data.type && item.name === data.name;

                  if (isHashtag && isTypeAndNameMatch) {
                      return acc
                          ? { ...acc, [watchlist]: item }
                          : { [watchlist]: item };
                  }

                  if (
                      isRelatedHashtag &&
                      isTypeAndNameMatch &&
                      matchRelatedMeta(item, data)
                  ) {
                      return acc
                          ? { ...acc, [watchlist]: item }
                          : { [watchlist]: item };
                  }

                  if (itemId === id) {
                      return acc
                          ? { ...acc, [watchlist]: item }
                          : { [watchlist]: item };
                  }

                  return acc;
              }, null)
            : null;
    const watching = !isLoading && foundItemsByWatchlist;

    const buttonProps = {
        fetching: isLoading,
        isDisabled: isLoading,
        IconLeft: watching ? MdPlaylistAddCheck : FiPlus,
        IconRight: FiChevronDown,
        size: 'sm',
        children: btnChildren,
        mixpanelMeta: {
            name: data && data.name,
        },
    };
    const successToastConfig = {
        icon: '✅',
        position: 'top-right',
        duration: 7000,
    };

    const latestModifiedByWatchlist =
        watchlistItems?.reduce((acc, item) => {
            const watchlistId = item.watchlist;
            const modified = item.modified || 0;

            if (!acc[watchlistId] || acc[watchlistId] < modified) {
                acc[watchlistId] = modified;
            }

            return acc;
        }, {}) || {};
    const sortedWatchlists =
        watchlists &&
        Object.values(watchlists).sort((a, b) => {
            const aModified = latestModifiedByWatchlist[a.id] || 0;
            const bModified = latestModifiedByWatchlist[b.id] || 0;
            return bModified - aModified;
        });
    const handleToastClick = (watchlistId) =>
        history.push(
            mutateToWithCountryParam(
                `/watchlist/${watchlistId}${data.type === 'hashtag' || data.type === 'related-hashtag' ? '/tiktok' : data.type === 'instagram-hashtag' || data.type === 'instagram-related-hashtag' ? '/instagram' : data.type === 'affinity' ? '/google' : ''}`,
            ),
        );
    return (
        <>
            {isLoading ||
            !watchlists ||
            (watchlists && Object.keys(watchlists).length < 1) ? (
                <IconButton
                    {...buttonProps}
                    onClick={(e) => setShowModal(true)}
                    css="margin-left: 16px;"
                    {...restProps}
                />
            ) : (
                <MenuButton
                    buttonProps={buttonProps}
                    options={[
                        {
                            key: NEW_WATCHLIST,
                            children: NEW_WATCHLIST,
                            IconLeft: FiPlus,
                            onClick: () => setShowModal(true),
                            mixpanelMeta: {
                                watchlist: NEW_WATCHLIST,
                                action: 'addWatchlist',
                            },
                            style: { fontWeight: 600 },
                        },
                        <MenuSeparator key="separator" />,
                        ...sortedWatchlists.map(({ id: watchlistId, name }) => {
                            const maybeItem = (foundItemsByWatchlist || {})[
                                watchlistId
                            ];
                            return {
                                key: watchlistId || id,
                                IconLeft: maybeItem
                                    ? MdCheckBox
                                    : MdCheckBoxOutlineBlank,
                                children: name,
                                onClick: () => {
                                    if (maybeItem) {
                                        removeItems([{ ...maybeItem }]);
                                    } else {
                                        toast.success(
                                            <span className="inline-flex items-center gap-2 leading-[1.4]">
                                                <span>Success!</span>
                                                <SoftButtonWhiteText
                                                    onClick={() =>
                                                        handleToastClick(
                                                            watchlistId,
                                                        )
                                                    }
                                                >
                                                    View Watchlist
                                                </SoftButtonWhiteText>
                                            </span>,
                                            successToastConfig,
                                        );
                                        addItems([
                                            {
                                                id,
                                                watchlist: watchlistId,
                                                data,
                                            },
                                        ]).catch((err) => {
                                            toast.error(
                                                'Error adding to watchlist',
                                            );
                                            console.error(err);
                                        });
                                    }
                                },
                                mixpanelMeta: {
                                    watchlist: name,
                                    action: maybeItem ? 'remove' : 'add',
                                },
                            };
                        }),
                    ]}
                    css="margin-left: 16px;"
                    {...restProps}
                />
            )}
            <WatchlistModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onConfirm={async (name) => {
                    const watchlistId = uuid();
                    toast.success(
                        <span className="inline-flex items-center gap-2 leading-[1.4]">
                            <span>Success!</span>
                            <SoftButtonWhiteText
                                onClick={() => handleToastClick(watchlistId)}
                            >
                                View Watchlist
                            </SoftButtonWhiteText>
                        </span>,
                        successToastConfig,
                    );
                    return Promise.all([
                        addWatchlist({
                            id: watchlistId,
                            data: { name: name || 'My Watchlist' },
                        }),
                        addItems([{ id, watchlist: watchlistId, data }]),
                    ]).catch((err) => {
                        toast.error('Error adding to watchlist');
                        console.error(err);
                    });
                }}
            />
        </>
    );
}

export default ToggleWatch;
